"use client";
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Confetti from 'react-confetti';
import TopNavThree from '../../components/Header/TopNav/TopNavThree';
import MenuFurniture from '../../components/Header/Menu/MenuFurniture';
import Footer from '../../components/Footer/Footer';
import Product from '../../components/Product/Product';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Scrollbar } from "swiper/modules";
import "swiper/css/bundle";
import productData from '../../data/Product.json';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { generateOrderNumber } from '../../utils/orderUtils';
import { tiktokTrackOrderPlaced } from '../../utils/tiktokPixel';
import { metaTrackOrderPlaced } from '../../utils/metaPixel';

const formatDateFrench = (date: Date) => {
  return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const OrderConfirmation = () => {
  console.log("OrderConfirmation component loaded");
  document.title = "Confirmation de commande | Kissariti";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get order number from URL parameters
  const orderNumberFromUrl = searchParams.get('orderNumber');
  const orderNumber = orderNumberFromUrl || generateOrderNumber();
  
  console.log("Order number from URL:", orderNumberFromUrl);
  console.log("Final order number:", orderNumber);
  
  const paymentMethod = searchParams.get('paymentMethod') || 'Non spécifié';
  const deliveryMethod = searchParams.get('deliveryMethod') || 'Non spécifié';
  const userCity = searchParams.get('city') || 'Non spécifiée';
  const userTelephone = searchParams.get('telephone') || 'Non spécifié';
  const userFullName = searchParams.get('fullName') || 'Non spécifié';
  
  // Use current date and estimated delivery date based on shipping method
  const now = new Date();
  let delivery = new Date(now);
  const shippingInfo = {
    method: deliveryMethod === 'express' ? 'Livraison Express' : deliveryMethod === 'standard' ? 'Livraison Standard' : deliveryMethod,
    payment: paymentMethod,
  };
  if (deliveryMethod === 'express') {
    // Same day delivery
    delivery = new Date(now);
  } else if (deliveryMethod === 'standard') {
    // 3 days later
    delivery.setDate(now.getDate() + 3);
  }

  const orderInfo = {
    date: formatDateFrench(now),
    status: 'En cours de traitement',
    deliveryDate: formatDateFrench(delivery),
  };
  const address = {
    name: userFullName,
    city: userCity,
    phone: userTelephone,
  };

  const [showConfetti, setShowConfetti] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [actualOrderNumber, setActualOrderNumber] = useState(orderNumber);
  const [cartItems, setCartItems] = useState<Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
    total: number;
    color?: string;
    size?: string;
    pointure?: string;
  }>>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [shippingFee, setShippingFee] = useState<number>(0);
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoDiscount, setPromoDiscount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    fullName: "",
    telephone: "",
    city: "",
    paymentMethod: "",
    deliveryMethod: ""
  });
  const hasSelectedVariantValue = (value?: string) => {
    if (!value) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "pas sélectionné" && str !== "0";
  };

  // Function to fetch order details from database
  const fetchOrderDetails = async (orderNum: string) => {
    try {
      const response = await fetch(`/api/orders/${orderNum}`);
      if (response.ok) {
        const orderData = await response.json();
        console.log("Order details from database:", orderData);
        if (orderData.orderNumber) {
          setActualOrderNumber(orderData.orderNumber);
        }
      } else {
        console.log("Order not found in database, using URL parameter");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };

  const generateReceipt = async () => {
    // Import jsPDF dynamically
    const jsPDF = (await import('jspdf')).default;
    const doc = new jsPDF();
    
    // Set font
    doc.setFont('helvetica');
    
    // Add logo from public/logo.png
    try {
      // Try multiple possible paths for the logo
      const possiblePaths = [
        '/logo.png',
        './logo.png',
        `${window.location.origin}/logo.png`,
        `${window.location.protocol}//${window.location.host}/logo.png`
      ];
      
      let logoResponse = null;
      let logoUrl = '';
      
      for (const path of possiblePaths) {
        try {
          console.log('Trying logo path:', path);
          logoResponse = await fetch(path);
          if (logoResponse.ok) {
            logoUrl = path;
            console.log('Logo found at:', path);
            break;
          }
        } catch (e) {
          console.log('Failed to fetch from:', path, e);
        }
      }
      
      if (!logoResponse || !logoResponse.ok) {
        throw new Error(`Logo not found in any of the attempted paths`);
      }
      
      const logoBlob = await logoResponse.blob();
      console.log('Logo blob size:', logoBlob.size);
      
      const logoBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            resolve(reader.result as string);
          } else {
            reject(new Error('Failed to convert logo to base64'));
          }
        };
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.readAsDataURL(logoBlob);
      });
      
      console.log('Logo base64 length:', logoBase64.length);
      // Position logo at top left with proper spacing
      doc.addImage(logoBase64, 'PNG', 15, 15, 45, 25);
      console.log('Logo added to PDF successfully');
    } catch (error) {
      console.error('Error loading logo:', error);
      // Continue without logo
    }
    
    // Add QR Code at top right
    try {
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(actualOrderNumber)}&color=eb1e85&bgcolor=ffffff`;
      const qrResponse = await fetch(qrCodeUrl);
      const qrBlob = await qrResponse.blob();
      const qrBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(qrBlob);
      });
      
      // Position QR code at top right (195 - 35 = 160 for right margin, 15 for top margin)
      doc.addImage(qrBase64, 'PNG', 160, 15, 35, 35);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('QR Code', 160, 55);
    } catch (error) {
      console.log('QR Code generation failed');
    }
    
    // Header section - moved down to accommodate logo and QR code with more spacing
    doc.setFontSize(24);
    doc.setTextColor(235, 30, 133); // Pink color
    doc.text('KISSARITI', 70, 35);
    
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('Reçu de commande', 70, 45);
    
    // Separator line - moved down to give more space after header and QR code
    doc.setDrawColor(235, 30, 133);
    doc.setLineWidth(0.5);
    doc.line(15, 60, 195, 60);
    
    // Order details section - adjusted spacing
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('INFORMATIONS DE COMMANDE', 15, 75);
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Numéro de commande: ${actualOrderNumber}`, 15, 85);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 15, 92);
    doc.text(`Heure: ${new Date().toLocaleTimeString('fr-FR')}`, 15, 99);
    
    // Separator line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(15, 105, 195, 105);
    
    // Customer information section - adjusted spacing
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('INFORMATIONS CLIENT', 15, 120);
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Nom: ${customerInfo.fullName}`, 15, 130);
    doc.text(`Téléphone: ${customerInfo.telephone}`, 15, 137);
    doc.text(`Ville: ${customerInfo.city}`, 15, 144);
    doc.text(`Livraison: ${customerInfo.deliveryMethod}`, 15, 151);
    doc.text(`Paiement: ${customerInfo.paymentMethod}`, 15, 158);
    if (promoCode) {
      doc.text(`Code promo utilisé:`, 15, 165);
      doc.text(`${promoCode}`, 15, 172);
    }
    
    // Separator line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(15, promoCode ? 182 : 168, 195, promoCode ? 182 : 168);
    
    // Items table section - adjusted spacing
    const startY = promoCode ? 197 : 183;
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('ARTICLES COMMANDÉS', 15, startY);
    
    // Table header
    doc.setFillColor(245, 245, 245);
    doc.rect(15, startY + 5, 180, 8, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Article', 17, startY + 11);
    doc.text('Qté', 100, startY + 11);
    doc.text('Prix unit.', 120, startY + 11);
    doc.text('Total', 160, startY + 11);
    
    // Table content
    doc.setFont('helvetica', 'normal');
    let currentY = startY + 20;
    let itemCount = 0;
    
    cartItems.forEach((item, index) => {
      // Check if we need a new page
      if (currentY > 250) {
        doc.addPage();
        currentY = 20;
        
        // Add table header on new page
        doc.setFillColor(245, 245, 245);
        doc.rect(15, currentY, 180, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('Article', 17, currentY + 6);
        doc.text('Qté', 100, currentY + 6);
        doc.text('Prix unit.', 120, currentY + 6);
        doc.text('Total', 160, currentY + 6);
        doc.setFont('helvetica', 'normal');
        currentY += 15;
      }
      
      // Alternate row colors
      if (itemCount % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(15, currentY - 2, 180, 8, 'F');
      }
      
      // Include selected variant label in receipt line (size/pointure + color)
      const variantParts: string[] = [];
      if (hasSelectedVariantValue(item.size)) variantParts.push(`T:${item.size}`);
      if (hasSelectedVariantValue(item.pointure)) variantParts.push(`P:${item.pointure}`);
      if (hasSelectedVariantValue(item.color)) variantParts.push(`C:${item.color}`);
      const variantLabel = variantParts.length > 0 ? ` (${variantParts.join(' · ')})` : '';
      const fullProductLabel = `${item.name}${variantLabel}`;
      const productName =
        fullProductLabel.length > 35
          ? fullProductLabel.substring(0, 32) + "..."
          : fullProductLabel;
      
      doc.text(productName, 17, currentY + 4);
      doc.text(item.quantity.toString(), 100, currentY + 4);
      doc.text(`${item.price.toFixed(2)} MAD`, 120, currentY + 4);
      doc.text(`${item.total.toFixed(2)} MAD`, 160, currentY + 4);
      
      currentY += 10;
      itemCount++;
    });
    
    // Table bottom border
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(15, currentY, 195, currentY);
    
    // Total section with delivery fee breakdown
    currentY += 10;
    
    // Calculate subtotal (sum of all articles only)
    const deliveryFee = shippingFee || (customerInfo.deliveryMethod === 'express' ? 50 : 30);
    const subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
    
    // Subtotal
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Sous-total:', 125, currentY + 4);
    doc.text(`${subtotal.toFixed(2)} MAD`, 160, currentY + 4);
    
    // Promo code discount (if applicable)
    if (promoDiscount > 0 && promoCode) {
      currentY += 8;
      doc.setTextColor(0, 128, 0); // Green color for discount
      doc.text(`Réduction promo:`, 125, currentY + 4);
      doc.text(`-${promoDiscount.toFixed(2)} MAD`, 160, currentY + 4);
      currentY += 8;
      doc.text(`code: (${promoCode})`, 125, currentY + 4);
      doc.setTextColor(0, 0, 0); // Reset to black
    }
    
    // Delivery fee
    currentY += 8;
    doc.text(`Livraison (${customerInfo.deliveryMethod === 'express' ? 'Express' : 'Standard'}):`, 125, currentY + 4);
    doc.text(`${deliveryFee.toFixed(2)} MAD`, 160, currentY + 4);
    
    // Total
    currentY += 8;
    doc.setFillColor(245, 245, 245);
    doc.rect(120, currentY - 2, 75, 8, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(235, 30, 133);
    doc.text('TOTAL:', 125, currentY + 4);
    doc.text(`${(subtotal - promoDiscount + deliveryFee).toFixed(2)} MAD`, 160, currentY + 4);
    
    // Separator line
    currentY += 15;
    doc.setDrawColor(235, 30, 133);
    doc.setLineWidth(0.5);
    doc.line(15, currentY, 195, currentY);
    
    // Footer section
    currentY += 15;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(15, currentY, 195, currentY);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Merci pour votre commande !', 15, currentY + 10);
    doc.text('Kissariti — Votre boutique de confiance', 15, currentY + 17);
    doc.text('Tél. : +212 664-045333', 15, currentY + 24);
    doc.text('Fixe : +212 520-065753', 15, currentY + 31);
    doc.text('Email : contact@kissariti.ma', 15, currentY + 38);
    doc.text('Adresse : Sidi Maarouf, Casablanca', 15, currentY + 45);
    
    return doc;
  };

  useEffect(() => {
    const initializePage = async () => {
      try {
        // Get parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const orderNum = urlParams.get('orderNumber');
        const fullName = urlParams.get('fullName');
        const telephone = urlParams.get('telephone');
        const city = urlParams.get('city');
        const paymentMethod = urlParams.get('paymentMethod');
        const deliveryMethod = urlParams.get('deliveryMethod');
        const cartDetailsParam = urlParams.get('cartDetails');
        const totalParam = urlParams.get('total');
        const shippingFeeParam = urlParams.get('shippingFee');
        const promoCodeParam = urlParams.get('promoCode');
        const promoDiscountParam = urlParams.get('promoDiscount');

        if (orderNum) {
          setActualOrderNumber(orderNum);
        }

        if (fullName || telephone || city || paymentMethod || deliveryMethod) {
          setCustomerInfo({
            fullName: fullName || "",
            telephone: telephone || "",
            city: city || "",
            paymentMethod: paymentMethod || "",
            deliveryMethod: deliveryMethod || ""
          });
        }

        // Get cart details from URL parameters
        if (cartDetailsParam) {
          try {
            const cartDetails = JSON.parse(decodeURIComponent(cartDetailsParam));
            setCartItems(cartDetails);
            console.log("Cart items from URL:", cartDetails);
          } catch (error) {
            console.error("Error parsing cart details from URL:", error);
          }
        }

        if (totalParam) {
          try {
            const total = parseFloat(decodeURIComponent(totalParam));
            setTotalAmount(total);
            console.log("Total amount from URL:", total);
          } catch (error) {
            console.error("Error parsing total from URL:", error);
          }
        }

        if (shippingFeeParam) {
          try {
            const shippingFee = parseFloat(decodeURIComponent(shippingFeeParam));
            setShippingFee(shippingFee);
            console.log("Shipping fee from URL:", shippingFee);
          } catch (error) {
            console.error("Error parsing shipping fee from URL:", error);
          }
        }

        if (promoCodeParam) {
          try {
            const promoCode = decodeURIComponent(promoCodeParam);
            setPromoCode(promoCode);
            console.log("Promo code from URL:", promoCode);
          } catch (error) {
            console.error("Error parsing promo code from URL:", error);
          }
        }

        if (promoDiscountParam) {
          try {
            const promoDiscount = parseFloat(decodeURIComponent(promoDiscountParam));
            setPromoDiscount(promoDiscount);
            console.log("Promo discount from URL:", promoDiscount);
          } catch (error) {
            console.error("Error parsing promo discount from URL:", error);
          }
        }

        // Try to fetch actual order details from database
        if (orderNum) {
          await fetchOrderDetails(orderNum);
        }

        setHasInitialized(true);
      } catch (error) {
        console.error("Error initializing confirmation page:", error);
        setHasInitialized(true);
      }
    };

    initializePage();
  }, []);

  // Trigger confetti effect when page is initialized
  useEffect(() => {
    if (hasInitialized) {
      // Scroll to top of the page
    window.scrollTo(0, 0);
      
      // Set window dimensions for confetti
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      
      // Trigger confetti effect
      setShowConfetti(true);
      
      // Stop confetti after 5 seconds
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [hasInitialized]);

  // TikTok conversion (backup if checkout event did not fire; deduped per order id)
  useEffect(() => {
    if (!hasInitialized || !actualOrderNumber) return;

    const subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
    const value = subtotal - promoDiscount + shippingFee;
    if (value <= 0 && cartItems.length === 0) return;

    const conversionContents =
      cartItems.length > 0
        ? cartItems.map((item) => ({
            content_id: String(item.id),
            content_name: item.name,
            content_type: "product",
            quantity: item.quantity,
            price: item.price,
          }))
        : [{ content_id: String(actualOrderNumber), content_type: "product" }];

    const conversionEmail = undefined;
    const conversionPhone =
      customerInfo.telephone && customerInfo.telephone !== "Non spécifié"
        ? customerInfo.telephone
        : userTelephone !== "Non spécifié"
          ? userTelephone
          : undefined;
    const conversionValue = value > 0 ? value : totalAmount;

    tiktokTrackOrderPlaced({
      orderId: String(actualOrderNumber),
      value: conversionValue,
      email: conversionEmail,
      phone: conversionPhone,
      externalId: String(actualOrderNumber),
      contents: conversionContents,
    });
    metaTrackOrderPlaced({
      orderId: String(actualOrderNumber),
      value: conversionValue,
      email: conversionEmail,
      phone: conversionPhone,
      externalId: String(actualOrderNumber),
      contents: conversionContents,
    });
  }, [hasInitialized, actualOrderNumber, cartItems, promoDiscount, shippingFee]);

    // Handle window resize for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleContinueShopping = () => {
    navigate("/shop");
  };

  // Generate QR code URL using a free QR code service
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(actualOrderNumber)}&color=eb1e85&bgcolor=ffffff`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <TopNavThree props="style-three bg-white" />
      <div id="header" className="relative max-sm:mb-5">
        <MenuFurniture props="bg-white" />
      </div>

      {/* React Confetti Effect */}
      {showConfetti && (
        <Confetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
          wind={0.05}
          colors={["#ff4370", "#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444"]}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 50, pointerEvents: 'none' }}
        />
      )}

      <main className="flex-1 flex flex-col items-center justify-start py-8 px-2 sm:px-4">
        {/* Loading state */}
        {!hasInitialized ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement de la confirmation...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Line Separator */}
            <div className="w-full max-w-5xl mb-8">
              <hr className="border-pink border-t-2" />
            </div>

        {/* Success Icon and Message */}
        <div className="flex flex-col items-center mb-8">
          {/* Lottie animation instead of check mark */}
          <div className="flex justify-center mb-4">
            <DotLottieReact
                      src="https://lottie.host/78a86440-3023-4a5a-adde-acbab7f7d525/kDWf7qWS5v.lottie"
              loop
              autoplay
              style={{ width: 180, height: 180 }}
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 text-center">Votre commande a été reçue avec succès !</h1>
          <p className="text-gray-600 text-center mb-1">Merci pour votre achat !</p>
                  <p className="text-gray-700 text-center text-sm">Votre numéro de commande est : <span className="font-bold text-primary">{actualOrderNumber}</span></p>
                </div>

                {/* Order Summary - Moved here */}
                {cartItems.length > 0 && (
                  <div className="w-full max-w-5xl mb-4">
                    <div className="rounded-xl shadow p-6" style={{ backgroundColor: "#fbfbfb" }}>
                      <h2 className="font-bold text-lg mb-4 text-gray-800">Résumé de votre commande</h2>
                      <div className="space-y-3">
                        {cartItems.map((item, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                <Icon.Package className="text-gray-500" size={20} />
                              </div>
                              <div>
                                <div className="font-medium text-gray-800">{item.name}</div>
                                <div className="text-sm text-gray-500">Quantité: {item.quantity}</div>
                                <div className="text-sm text-gray-500">Prix unitaire: {item.price.toFixed(2)} MAD</div>
                                {(hasSelectedVariantValue(item.size) ||
                                  hasSelectedVariantValue(item.pointure) ||
                                  hasSelectedVariantValue(item.color)) && (
                                  <div className="mt-1 flex flex-wrap gap-1.5">
                                    {hasSelectedVariantValue(item.size) && (
                                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                        Taille: {item.size}
                                      </span>
                                    )}
                                    {hasSelectedVariantValue(item.pointure) && (
                                      <span className="rounded-full bg-cyan-100 px-2 py-0.5 text-[11px] font-medium text-cyan-700">
                                        Pointure: {item.pointure}
                                      </span>
                                    )}
                                    {hasSelectedVariantValue(item.color) && (
                                      <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-medium text-purple-700">
                                        Couleur: {item.color}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-800">{item.total.toFixed(2)} MAD</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Sous-total</span>
                          <span className="font-medium text-gray-800">{cartItems.reduce((sum, item) => sum + item.total, 0).toFixed(2)} MAD</span>
                        </div>
                        {promoDiscount > 0 && promoCode && (
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-green-600 font-medium">Réduction promo:</span>
                              <span className="text-green-600 font-medium">-{promoDiscount.toFixed(2)} MAD</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-green-600 text-sm">code: ({promoCode})</span>
                              <span></span>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Livraison ({customerInfo.deliveryMethod === 'express' ? 'Express' : 'Standard'})</span>
                          <span className="font-medium text-gray-800">{shippingFee.toFixed(2)} MAD</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                          <span className="font-semibold text-gray-800">Total de la commande</span>
                          <span className="font-bold text-lg text-pink">{(cartItems.reduce((sum, item) => sum + item.total, 0) - promoDiscount + shippingFee).toFixed(2)} MAD</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Download Receipt Button - Moved here */}
                <div className="w-full max-w-5xl mb-4">
                  <div className="rounded-xl shadow p-6" style={{ backgroundColor: "#fbfbfb" }}>
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div>
                        <h2 className="font-bold text-lg text-gray-800 mb-2">Télécharger votre reçu</h2>
                        <p className="text-gray-600 text-sm">Conservez une copie de votre reçu pour vos archives</p>
                      </div>
                      <button
                        onClick={async () => {
                          setIsDownloading(true);
                          try {
                            // Show loading state for 4 seconds to simulate processing
                            await new Promise(resolve => setTimeout(resolve, 2000));
                            
                            const doc = await generateReceipt();
                            doc.save(`recu-${actualOrderNumber}.pdf`);
                          } catch (error) {
                            console.error("Error downloading receipt:", error);
                          } finally {
                            setIsDownloading(false);
                          }
                        }}
                        disabled={isDownloading}
                        className="bg-pink text-white px-6 py-3 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isDownloading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Génération du reçu...
                          </>
                        ) : (
                          <>
                            <Icon.DownloadSimple size={20} />
                            Télécharger le reçu
                          </>
                        )}
                      </button>
                    </div>
                  </div>
        </div>

        {/* Responsive 3-column grid for info */}
        <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Order Info */}
                  <div className="rounded-xl shadow p-6 flex flex-col justify-between min-h-[180px]" style={{ backgroundColor: "#fbfbfb" }}>
            <h2 className="font-bold text-lg mb-2 text-gray-800">Informations de la commande</h2>
            <div className="text-gray-700 text-sm space-y-1">
              <div>Date de la commande : <span className="font-medium">{orderInfo.date}</span></div>
              <div>Statut : <span className="font-medium">{orderInfo.status}</span></div>
              <div>Date de livraison estimée : <span className="font-medium">{orderInfo.deliveryDate}</span></div>
            </div>
          </div>
          {/* Shipping Info */}
                  <div className="rounded-xl shadow p-6 flex flex-col justify-between min-h-[180px]" style={{ backgroundColor: "#fbfbfb" }}>
            <h2 className="font-bold text-lg mb-2 text-gray-800">Informations de livraison</h2>
            <div className="text-gray-700 text-sm space-y-1">
              <div>Méthode de livraison : <span className="font-medium">{shippingInfo.method}</span></div>
              <div>Méthode de paiement : <span className="font-medium">{shippingInfo.payment}</span></div>
              {promoCode && (
                <div>Code promo utilisé : <span className="font-medium text-green-600">{promoCode}</span></div>
              )}
            </div>
          </div>
          {/* QR Code */}
                  <div className="rounded-xl shadow p-6 flex flex-col items-center justify-center min-h-[180px]" style={{ backgroundColor: "#fbfbfb" }}>
            <h2 className="font-bold text-lg mb-4 text-gray-800">QR Code de la commande</h2>
            <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32" />
          </div>
        </div>
        {/* Shipping Address Full Width */}
        <div className="w-full max-w-5xl">
                  <div className="rounded-xl shadow p-6 mt-2" style={{ backgroundColor: "#fbfbfb" }}>
            <h2 className="font-bold text-lg mb-2 text-gray-800">Coordonnées de livraison</h2>
            <div className="text-gray-700 text-sm space-y-1">
              <div>{address.name}</div>
              <div>Ville : {address.city}</div>
              <div>Téléphone : {address.phone}</div>
            </div>
          </div>
        </div>

                {/* Continue Shopping Button */}
                <div className="w-full max-w-5xl mt-4">
                  <div className="rounded-xl shadow p-6" style={{ backgroundColor: "#fbfbfb" }}>
                    <div className="text-center">
                      <button
                        onClick={handleContinueShopping}
                        className="bg-pink text-white px-8 py-3 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center gap-2 mx-auto"
                      >
                        <Icon.ShoppingBag size={20} />
                        Continuer vos achats
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
      </main>
      
      <Footer />
    </div>
  );
};

export default OrderConfirmation; 